import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import ApplianceForm from '#component/warehouse/appliance-form/form';
import { updateBreadcrumb } from '#action/breadcrumb';
import { updateAppliance, fetchAppliance, clear } from '#action/warehouse/appliance/appliance';
import { putApplianceDevices, fetchApplianceDevicesDetailed, applianceDevicesClear } from '#action/warehouse/appliance/appliance-devices';
import { clearDevicesAvailable } from '#action/device/device-avaliable';
import PreloaderComponent from '#component/preloader';
import { validDataForSelect } from '#component/warehouse/appliance-form/form/helper';
import { setDocumentTitle } from '#helper';

const EditApplianceForm = props => {
  const { match: { params: { applianceId } }, history } = props;
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const account = useSelector(s => s.account, shallowEqual);
  const { appliance } = useSelector(s => s.appliance, shallowEqual);
  const { applianceDevices } = useSelector(s => s.applianceDevices, shallowEqual);

  const applianceWarehouse = useMemo(() => {
    if (appliance.warehouse) {
      const [wh] = validDataForSelect([{ ...appliance.warehouse, project: appliance?.project }]);

      return wh;
    }

    return {};
  }, [appliance.project, appliance.warehouse]);

  const initialValues = useMemo(() => ({
    label: appliance.label,
    warehouse: applianceWarehouse,
    devicesGroup: applianceDevices.map(a =>
      ({
        id: a.id,
        groupName: a.label,
        projectAlertId: a.projectAlertId,
        devices: a.devices.map(device =>
          ({
            value: device.id,
            label: device.title,
            alertOn: !!device.projectAlertId,
            showAlertIcon: a.projectAlertId,
            alertLabel: 'Switch on/off',
            id: device.id,
            title: device.title,
            uid: device.uid,
            type: device.type
          }))
      }))
  }), [appliance, applianceDevices, applianceWarehouse]);

  useEffect(() => {
    setDocumentTitle('Edit Application', appliance.label);
    if (applianceId && appliance && !loading) {
      dispatch(
        updateBreadcrumb([
          { label: 'Sites', path: '/warehouse' },
          { label: `${appliance.label}`, path: `/warehouse/appliance-overview/${applianceId}` },
          { label: 'Edit application' }
        ])
      );
    }
  }, [dispatch, appliance, applianceId, loading]);

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        dispatch(fetchAppliance(applianceId)),
        dispatch(fetchApplianceDevicesDetailed(applianceId))
      ]);
      setLoading(false);
    };

    fetchData();

    return () => {
      dispatch(clearDevicesAvailable());
      dispatch(clear());
      dispatch(applianceDevicesClear());
    };
  }, [dispatch, applianceId]);

  const handleFormSubmit = async values => {
    const {
      label,
      warehouse,
      devicesGroup
    } = values;

    const data = {
      label,
      warehouseId: +warehouse
    };

    const calls = [
      dispatch(updateAppliance(applianceId, data)),
      dispatch(putApplianceDevices(applianceId, devicesGroup ?? []))
    ];

    const success = await Promise.all(calls)
      .then(([aStatus, dStatus]) => aStatus.success && dStatus.success);

    if (success) {
      await history.push(`/warehouse/appliance-overview/${applianceId}`);
    }
    return { success };
  };

  return (
    !loading ? (
      <ApplianceForm
        title="Edit application"
        {...props}
        initialValues={initialValues}
        handleFormSubmit={handleFormSubmit}
        account={account}
      />
    ) : (
      <PreloaderComponent />
    )
  );
};

export default EditApplianceForm;
